import { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle2, XCircle, Loader2, Sparkles,
  Plus, Trash2, Pencil, Power, PowerOff, Users, RefreshCw,
} from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import AiProviderFormModal, {
  PROVIDER_META,
  buildAiProviderPayload,
  type AiProviderFormValue,
} from './AiProviderFormModal';
import { useUIStore } from '@/stores/ui-store';
import ProviderBudgetPanel from './ProviderBudgetPanel';

interface PlatformProvider {
  id: string;
  name: string;
  slug: string;
  provider_type: string;
  capabilities: string[];
  api_base_url: string | null;
  account_id: string | null;
  api_key_masked: string;
  has_api_key: boolean;
  model_config: Record<string, any>;
  text_priority: number;
  image_priority: number;
  is_active: boolean;
  // Pre-fills the edit modal's tier; without it, an edit resets 'paid' to 'free'.
  cost_tier?: string;
}

interface GrantRow {
  provider_id: string;
  site_id: string;
  granted_at: string;
}

interface SiteOption {
  site_id: string;
  workspace_name?: string | null;
}

interface AutoDiscoveryResult {
  refreshed: boolean;
  model_count: number;
  derived_capabilities: string[];
}

type TestStatus = 'idle' | 'testing' | 'success' | 'error';

export default function PlatformAiProvidersSection() {
  const [providers, setProviders] = useState<PlatformProvider[]>([]);
  const [grants, setGrants] = useState<GrantRow[]>([]);
  const [sites, setSites] = useState<SiteOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState<PlatformProvider | null>(null);
  const [saving, setSaving] = useState(false);
  const [testStatuses, setTestStatuses] = useState<Record<string, TestStatus>>({});
  const [testErrors, setTestErrors] = useState<Record<string, string>>({});
  const [expandedProviderId, setExpandedProviderId] = useState<string | null>(null);
  const addToast = useUIStore((s) => s.addToast);

  useEffect(() => {
    // Auto-run the connection test for every provider on load so the status
    // dots reflect real key validity at a glance — a silently-invalid key (e.g.
    // an expired GOOGLE_API_KEY) shows red without the admin clicking Test on
    // each one. Same pattern the codebase uses (loadAll not in deps).
    loadAll().then((ps) => ps.forEach((p) => void handleTestConnection(p.id)));
  }, []);

  async function loadAll(): Promise<PlatformProvider[]> {
    try {
      setLoading(true);
      const [pRes, gRes] = await Promise.all([
        api.get<{ providers: PlatformProvider[] }>('/admin/platform/ai/providers'),
        api.get<{ grants: GrantRow[]; sites: SiteOption[] }>('/admin/platform/ai/grants'),
      ]);
      setProviders(pRes.providers || []);
      setGrants(gRes.grants || []);
      setSites(gRes.sites || []);
      return pRes.providers || [];
    } catch (err) {
      console.error('Failed to load platform AI providers:', err);
      addToast('Failed to load platform AI providers', 'error');
      return [];
    } finally {
      setLoading(false);
    }
  }

  // Re-test every provider (header "Recheck keys" button).
  function recheckAll() {
    providers.forEach((p) => void handleTestConnection(p.id));
  }

  function openAddDialog() {
    setEditing(null);
    setShowDialog(true);
  }

  function openEditDialog(provider: PlatformProvider) {
    setEditing(provider);
    setShowDialog(true);
  }

  async function handleSubmit(value: AiProviderFormValue) {
    setSaving(true);
    try {
      const payload = buildAiProviderPayload(value, { editing: !!editing });

      let response: { provider?: PlatformProvider & { auto_discovered?: AutoDiscoveryResult } };
      if (editing) {
        response = await api.put(`/admin/platform/ai/providers/${editing.id}`, payload);
      } else {
        response = await api.post('/admin/platform/ai/providers', payload);
      }
      const auto = response.provider?.auto_discovered;
      if (auto) {
        if (auto.refreshed && auto.model_count > 0) {
          const caps = auto.derived_capabilities.length > 0 ? auto.derived_capabilities.join(', ') : 'none';
          addToast(
            `${editing ? 'Updated' : 'Added'} — discovered ${auto.model_count} models (${caps})`,
            'success',
          );
        } else {
          // Same fault as the tenant section: a zero-model discovery reported
          // as a success, with a promise of a retry that can be permanently
          // untrue when the cause is a fetcher that never makes a request.
          addToast(
            `${editing ? 'Updated' : 'Added'}, but no models were discovered — this provider cannot serve requests yet. ` +
              `The hourly refresh will retry; if it stays empty, the key or the catalog fetch is failing.`,
            'warning',
          );
        }
      } else {
        addToast(`Platform provider ${editing ? 'updated' : 'added'}`, 'success');
      }
      setShowDialog(false);
      await loadAll();
    } catch (err: any) {
      addToast(err.message || 'Failed to save platform provider', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Delete this platform provider? All grants will be removed.')) return;
    try {
      await api.delete(`/admin/platform/ai/providers/${id}`);
      addToast('Platform provider deleted', 'success');
      await loadAll();
    } catch {
      addToast('Failed to delete platform provider', 'error');
    }
  }

  async function handleToggleActive(provider: PlatformProvider) {
    try {
      await api.put(`/admin/platform/ai/providers/${provider.id}`, { is_active: !provider.is_active });
      await loadAll();
    } catch {
      addToast('Failed to update platform provider', 'error');
    }
  }

  async function handleTestConnection(id: string) {
    setTestStatuses((s) => ({ ...s, [id]: 'testing' }));
    setTestErrors((s) => ({ ...s, [id]: '' }));
    try {
      const res = await api.post<{ ok: boolean; error?: string }>(`/admin/platform/ai/providers/${id}/test`);
      setTestStatuses((s) => ({ ...s, [id]: res.ok ? 'success' : 'error' }));
      if (!res.ok) setTestErrors((s) => ({ ...s, [id]: res.error || 'Test failed' }));
    } catch {
      setTestStatuses((s) => ({ ...s, [id]: 'error' }));
      setTestErrors((s) => ({ ...s, [id]: 'Test request failed' }));
    }
  }

  const isGranted = useCallback(
    (providerId: string, siteId: string) => grants.some((g) => g.provider_id === providerId && g.site_id === siteId),
    [grants],
  );

  // Optimistic grant toggle: update local `grants` immediately and fire the
  // request in the background. Avoids the full loadAll() refetch, which flipped
  // the global loading state and flashed the whole section to a spinner on
  // every click. Reverts on failure.
  async function toggleGrant(providerId: string, siteId: string) {
    const wasGranted = isGranted(providerId, siteId);
    setGrants((prev) =>
      wasGranted
        ? prev.filter((g) => !(g.provider_id === providerId && g.site_id === siteId))
        : [...prev, { provider_id: providerId, site_id: siteId, granted_at: new Date().toISOString() }],
    );
    try {
      if (wasGranted) {
        await api.delete(`/admin/platform/ai/grants/${providerId}/${encodeURIComponent(siteId)}`);
      } else {
        await api.post('/admin/platform/ai/grants', { provider_id: providerId, site_id: siteId });
      }
    } catch {
      // Revert the optimistic change.
      setGrants((prev) =>
        wasGranted
          ? [...prev, { provider_id: providerId, site_id: siteId, granted_at: new Date().toISOString() }]
          : prev.filter((g) => !(g.provider_id === providerId && g.site_id === siteId)),
      );
      addToast('Failed to update grant', 'error');
    }
  }

  // Grant or revoke this provider across every site in one call. Optimistic,
  // same as the per-site toggle.
  async function setAllGrants(providerId: string, granted: boolean) {
    const prevGrants = grants;
    setGrants((prev) => {
      const others = prev.filter((g) => g.provider_id !== providerId);
      if (!granted) return others;
      const now = new Date().toISOString();
      return [...others, ...sites.map((s) => ({ provider_id: providerId, site_id: s.site_id, granted_at: now }))];
    });
    try {
      await api.put(`/admin/platform/ai/providers/${providerId}/grants/all`, { granted });
    } catch {
      setGrants(prevGrants);
      addToast('Failed to update grants', 'error');
    }
  }

  if (loading) {
    return (
      <Card padding="lg" className="mb-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </div>
      </Card>
    );
  }

  return (
    <Card padding="lg" className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-500" />
          <h3 className="text-lg font-semibold text-gray-900">Platform AI Providers</h3>
        </div>
        <div className="flex items-center gap-2">
          {providers.length > 0 && (
            <Button
              variant="secondary"
              size="sm"
              type="button"
              onClick={recheckAll}
              title="Re-test every provider's connection"
            >
              <RefreshCw className="w-4 h-4 mr-1" /> Recheck keys
            </Button>
          )}
          <Button variant="primary" size="sm" type="button" onClick={openAddDialog}>
            <Plus className="w-4 h-4 mr-1" /> Add Provider
          </Button>
        </div>
      </div>

      <p className="text-sm text-gray-500 mb-4">
        Platform-owned API keys. Grant access per site below — granted sites will use these as a
        fallback when their own provider for the same type is missing or fails. Tenant admins
        never see the key value.
      </p>

      {providers.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p className="font-medium">No platform AI providers configured</p>
          <p className="text-sm mt-1">Add a provider to make platform-owned keys available to tenant sites.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {providers.map((provider) => {
            const models = (provider.model_config?.models || {}) as Record<string, string>;
            const grantedSites = grants.filter((g) => g.provider_id === provider.id);
            const isExpanded = expandedProviderId === provider.id;
            return (
              <div
                key={provider.id}
                className={`rounded-lg border transition-colors ${
                  !provider.is_active ? 'border-gray-200 bg-gray-50 opacity-60' :
                  'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3 px-3 py-2.5">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                          testStatuses[provider.id] === 'success' ? 'bg-green-500'
                          : testStatuses[provider.id] === 'error' ? 'bg-red-500'
                          : testStatuses[provider.id] === 'testing' ? 'bg-amber-400 animate-pulse'
                          : 'bg-gray-300'
                        }`}
                        title={
                          testStatuses[provider.id] === 'success' ? 'Connection OK — key valid'
                          : testStatuses[provider.id] === 'error' ? `Broken: ${testErrors[provider.id] || 'connection failed'}`
                          : testStatuses[provider.id] === 'testing' ? 'Testing…'
                          : 'Not checked yet'
                        }
                        aria-label={`Key status: ${testStatuses[provider.id] || 'unknown'}`}
                      />
                      <span className="font-medium text-sm text-gray-900 truncate">{provider.name}</span>
                      <span className="text-xs text-gray-400">({provider.provider_type})</span>
                      {!provider.is_active && (
                        <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">Disabled</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {models.text && <span className="text-xs text-gray-500 font-mono">text: {models.text}</span>}
                      {models.image && <span className="text-xs text-gray-500 font-mono">image: {models.image}</span>}
                      {provider.has_api_key && (
                        <span className="text-xs text-gray-400">Key: {provider.api_key_masked}</span>
                      )}
                      {!provider.has_api_key && PROVIDER_META[provider.provider_type]?.requiresKey && (
                        <span className="text-xs text-amber-600">No API key</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setExpandedProviderId(isExpanded ? null : provider.id)}
                      className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                        grantedSites.length > 0
                          ? 'text-purple-700 bg-purple-50 hover:bg-purple-100'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                      title="Manage grants"
                    >
                      <Users className="w-3.5 h-3.5" />
                      {grantedSites.length} site{grantedSites.length === 1 ? '' : 's'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleTestConnection(provider.id)}
                      disabled={testStatuses[provider.id] === 'testing'}
                      className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                      title="Test connection"
                    >
                      {testStatuses[provider.id] === 'testing' && <Loader2 className="w-4 h-4 animate-spin" />}
                      {testStatuses[provider.id] === 'success' && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                      {testStatuses[provider.id] === 'error' && <XCircle className="w-4 h-4 text-red-500" />}
                      {(!testStatuses[provider.id] || testStatuses[provider.id] === 'idle') && <CheckCircle2 className="w-4 h-4" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleToggleActive(provider)}
                      className={`p-1.5 rounded ${provider.is_active ? 'text-green-600 hover:text-green-700' : 'text-gray-400 hover:text-gray-600'}`}
                      title={provider.is_active ? 'Disable' : 'Enable'}
                    >
                      {provider.is_active ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => openEditDialog(provider)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                      title="Edit"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(provider.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {isExpanded && (() => {
                  const allGranted = sites.length > 0 && grantedSites.length >= sites.length;
                  return (
                  <div className="px-3 pb-3 pt-1 border-t border-gray-100 bg-gray-50/40">
                    <ProviderBudgetPanel providerId={provider.id} />
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <p className="text-xs text-gray-500">
                        Toggle which sites are allowed to fall back to this provider when their own key is missing or failing.
                      </p>
                      {sites.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setAllGrants(provider.id, !allGranted)}
                          className="flex-shrink-0 text-xs font-medium text-purple-700 hover:text-purple-900 hover:underline whitespace-nowrap"
                        >
                          {allGranted ? 'Clear all' : 'Select all'}
                        </button>
                      )}
                    </div>
                    {sites.length === 0 ? (
                      <p className="text-xs text-amber-600">No sites discovered. Make sure the platform DB is reachable.</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                        {sites.map((site) => (
                          <label
                            key={site.site_id}
                            className="flex items-center gap-2 p-1.5 rounded hover:bg-white cursor-pointer text-sm"
                          >
                            <input
                              type="checkbox"
                              checked={isGranted(provider.id, site.site_id)}
                              onChange={() => toggleGrant(provider.id, site.site_id)}
                              className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                            />
                            <span className="text-gray-800 truncate">
                              {site.site_id}
                              {site.workspace_name && (
                                <span className="text-xs text-gray-400 ml-1">({site.workspace_name})</span>
                              )}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}

      {Object.entries(testErrors).filter(([, v]) => v).map(([id, err]) => (
        <div key={id} className="mt-2 text-xs text-red-600">
          {providers.find((p) => p.id === id)?.name}: {err}
        </div>
      ))}

      <AiProviderFormModal
        isOpen={showDialog}
        onClose={() => setShowDialog(false)}
        editing={editing}
        saving={saving}
        onSubmit={handleSubmit}
        titleAdd="Add Platform Provider"
        titleEdit="Edit Platform Provider"
      />
    </Card>
  );
}
